import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, Typography, Avatar, Progress, Dropdown, Modal, Input, Radio, message, Row, Col, Menu as AntMenu, ConfigProvider, Drawer, List, Divider, Badge, Tabs, Spin, Empty, Card, Statistic, Space, Tooltip, Alert, Descriptions, Tag as AntTag } from "antd";
import {
  LogoutOutlined,
  ClockCircleOutlined,
  DownOutlined,
  UpOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  MenuOutlined,
  UserOutlined,
  SettingOutlined,
  KeyOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  BellOutlined,
  FolderOutlined,
  FileOutlined,
  BarChartOutlined,
  BgColorsOutlined,
  CheckOutlined,
  CloseOutlined,
  HomeOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ApartmentOutlined,
  BankOutlined
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const { Header } = Layout;
const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

const NAV_BLUE = "#032452";
const INACTIVITY_TIMEOUT = 60 * 60 * 3000;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

// Predefined themes
const THEMES = {
  ocean: {
    name: "Ocean Blue",
    background: "linear-gradient(135deg, #000428 0%, #004e92 25%, #1a237e 50%, #004d40 75%, #004e92 100%)",
    buttonBg: "rgba(66, 153, 225, 0.25)",
    buttonBorder: "rgba(66, 153, 225, 0.5)",
    menuSelected: "rgba(66, 153, 225, 0.3)",
    iconColor: "#4da6ff",
    primary: "#3182ce"
  },
  sunset: {
    name: "Sunset Pink",
    background: "linear-gradient(135deg, #ff6b6b 0%, #f093fb 25%, #f5576c 50%, #f093fb 75%, #ff6b6b 100%)",
    buttonBg: "rgba(240, 147, 251, 0.25)",
    buttonBorder: "rgba(240, 147, 251, 0.5)",
    menuSelected: "rgba(240, 147, 251, 0.3)",
    iconColor: "#f093fb",
    primary: "#f5576c"
  },
  forest: {
    name: "Forest Green",
    background: "linear-gradient(135deg, #134e5e 0%, #71b280 25%, #2e8b57 50%, #71b280 75%, #134e5e 100%)",
    buttonBg: "rgba(113, 178, 128, 0.25)",
    buttonBorder: "rgba(113, 178, 128, 0.5)",
    menuSelected: "rgba(113, 178, 128, 0.3)",
    iconColor: "#71b280",
    primary: "#2e8b57"
  },
  purple: {
    name: "Royal Purple",
    background: "linear-gradient(135deg, #4a00e0 0%, #8e2de2 25%, #6a11cb 50%, #8e2de2 75%, #4a00e0 100%)",
    buttonBg: "rgba(142, 45, 226, 0.25)",
    buttonBorder: "rgba(142, 45, 226, 0.5)",
    menuSelected: "rgba(142, 45, 226, 0.3)",
    iconColor: "#8e2de2",
    primary: "#6a11cb"
  },
  crimson: {
    name: "Crimson Red",
    background: "linear-gradient(135deg, #870000 0%, #dc2430 25%, #b91c1c 50%, #dc2430 75%, #870000 100%)",
    buttonBg: "rgba(220, 36, 48, 0.25)",
    buttonBorder: "rgba(220, 36, 48, 0.5)",
    menuSelected: "rgba(220, 36, 48, 0.3)",
    iconColor: "#dc2430",
    primary: "#b91c1c"
  },
  teal: {
    name: "Teal Dream",
    background: "linear-gradient(135deg, #0f4c5c 0%, #1a7f7f 25%, #0e6b6b 50%, #1a7f7f 75%, #0f4c5c 100%)",
    buttonBg: "rgba(26, 127, 127, 0.25)",
    buttonBorder: "rgba(26, 127, 127, 0.5)",
    menuSelected: "rgba(26, 127, 127, 0.3)",
    iconColor: "#1a7f7f",
    primary: "#0e6b6b"
  },
  dark: {
    name: "Dark Mode",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #16213e 75%, #1a1a2e 100%)",
    buttonBg: "rgba(255, 255, 255, 0.15)",
    buttonBorder: "rgba(255, 255, 255, 0.3)",
    menuSelected: "rgba(255, 255, 255, 0.2)",
    iconColor: "#e94560",
    primary: "#e94560"
  }
};

const styles = {
  fadeInDown: {
    animation: 'fadeInDown 0.2s ease'
  },
  slideDown: {
    animation: 'slideDown 0.3s ease'
  },
  spin: {
    animation: 'spin 1s linear infinite'
  }
};

const addStylesToHead = () => {
  if (document.getElementById('navbar-styles')) return;

  const styleElement = document.createElement('style');
  styleElement.id = 'navbar-styles';
  styleElement.innerHTML = `
    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    @keyframes twinkle {
      0%, 100% { opacity: 0.2; }
      50% { opacity: 0.8; }
    }
    
    @keyframes glowPulse {
      0%, 100% { opacity: 0.6; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.1); }
    }
    
    .user-info-scroll::-webkit-scrollbar {
      width: 4px;
    }
    
    .user-info-scroll::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    
    .user-info-scroll::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
    
    .user-info-scroll::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }

    .mobile-menu-item {
      padding: 12px 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      border-bottom: 1px solid #f0f0f0;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #ffffff;
    }
    
    .mobile-menu-item:hover {
      background: #f0f7ff;
    }

    .group-dropdown-custom .ant-dropdown-menu {
      padding: 8px 0;
      border-radius: 8px;
      box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    }
    
    .group-dropdown-custom .ant-dropdown-menu-item {
      padding: 10px 16px;
      transition: all 0.2s ease;
    }
    
    .group-dropdown-custom .ant-dropdown-menu-item:hover {
      background: #f0f7ff;
    }

    .group-button {
      background: rgba(255, 255, 255, 0.08) !important;
      border: 1px solid rgba(255, 255, 255, 0.15) !important;
      color: #ffffff !important;
      font-weight: 500;
      transition: all 0.2s ease;
      padding: 0 14px;
      height: 34px;
      line-height: 34px;
      border-radius: 6px;
      white-space: nowrap;
    }
    
    .group-button:hover {
      background: rgba(255, 255, 255, 0.2) !important;
      transform: translateY(-1px);
      border-color: rgba(255, 255, 255, 0.4) !important;
    }

    .navbar-menu-container {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      position: relative;
      z-index: 1;
      margin: 0 16px;
      overflow-x: auto;
      flex-wrap: nowrap;
      min-height: 44px;
      background: rgba(255, 255, 255, 0.06);
      border-radius: 10px;
      padding: 4px 12px;
      border: 1px solid rgba(255, 255, 255, 0.08);
    }

    .navbar-menu-container::-webkit-scrollbar {
      height: 2px;
    }
    
    .navbar-menu-container::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 2px;
    }
    
    .navbar-menu-container::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      .ant-drawer-body {
        padding: 0 !important;
      }
      
      .ant-drawer-title {
        font-size: 16px !important;
      }
    }
  `;
  document.head.appendChild(styleElement);
};

const Navbar = ({ onMenuClick, language: propLanguage, onLanguageChange }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [screenTime, setScreenTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [inactivityTimeLeft, setInactivityTimeLeft] = useState(INACTIVITY_TIMEOUT);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);

  // Combined Notification States
  const [notificationsDrawerVisible, setNotificationsDrawerVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [legacyUnreadCount, setLegacyUnreadCount] = useState(0);
  // Tab 1: Legacy Notification Dropdown Data
  const [legacyNotifications, setLegacyNotifications] = useState([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyAllNotifications, setLegacyAllNotifications] = useState([]);
  const [filteredLegacyView, setFilteredLegacyView] = useState([]);
  const [legacyFilterType, setLegacyFilterType] = useState("all");

  // Tab 2: Workflow Notification Panel Data
  const [workflowAllNotifications, setWorkflowAllNotifications] = useState([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowFilterType, setWorkflowFilterType] = useState("all");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Theme state - load from sessionStorage
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = sessionStorage.getItem("navbar_theme");
    return savedTheme && THEMES[savedTheme] ? savedTheme : "ocean";
  });

  // Sidebar menu items state
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [groupedButtons, setGroupedButtons] = useState([]);
  const [navbarTheme, setNavbarTheme] = useState(null);
  const [navbarTitle, setNavbarTitle] = useState('DMA Repository Portal');

  // Password states
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const [passwordVerificationMethod, setPasswordVerificationMethod] = useState("email");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState("");

  // Language state
  const [isMarathi, setIsMarathi] = useState(() => {
    if (propLanguage !== undefined) {
      return propLanguage === "marathi";
    }
    return sessionStorage.getItem("dma_portal_language") === "MR";
  });

  const sessionStartRef = useRef(Date.now());
  const profileRef = useRef(null);
  const themeDropdownRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Get current theme object
  const theme = THEMES[currentTheme];

  // Save theme to sessionStorage when changed
  useEffect(() => {
    sessionStorage.setItem("navbar_theme", currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    if (propLanguage !== undefined) {
      setIsMarathi(propLanguage === "marathi");
    }
  }, [propLanguage]);

  // Helper function to check if notification is within last 7 days
  const isWithinLast7Days = useCallback((createdAt) => {
    if (!createdAt) return false;
    const notificationDate = new Date(createdAt);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate - notificationDate);
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  }, []);

  const TEXT = {
    EN: {
      portal: "DMA Repository Portal",
      signOut: "Sign Out",
      sessionDuration: "Session Duration",
      todayDate: "Today's Date",
      currentTime: "Current Time",
      autoLogout: "Auto-logout in",
      loading: "Loading...",
      logoutWarning: "You will be logged out due to inactivity in",
      profile: "Profile",
      settings: "Settings",
      changePassword: "Change Password",
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmPassword: "Confirm New Password",
      verificationMethod: "Verification Method",
      emailVerification: "Email Verification",
      dscVerification: "DSC Verification",
      sendCode: "Send Verification Code",
      verifyAndChange: "Verify & Change Password",
      verificationCode: "Verification Code",
      passwordRequirements: "Password must be at least 8 characters, include uppercase, number, and special character",
      passwordMismatch: "Passwords do not match",
      passwordSameAsCurrent: "New password cannot be the same as current password",
      passwordPreviouslyUsed: "Password has been used before. Please choose a different password",
      codeSent: "Verification code sent",
      dscInstruction: "Please insert your DSC and click 'Verify' to proceed",
      cancel: "Cancel",
      chooseTheme: "Choose Theme",
      themes: "Themes",
      menu: "Menu",
      notifications: "Notifications",
      allNotifications: "All Notifications",
      markAllRead: "Mark all as read",
      noNotifications: "No notifications",
      viewAll: "View All",
      sentBy: "Sent by",
      markRead: "Mark as Read",
      read: "Read",
      close: "Close",
      new: "NEW",
      total: "Total",
      approved: "Approved",
      rejected: "Rejected",
      escalated: "Escalated",
      pending: "Pending",
      legacyNotifications: "General Notifications",
      workflowNotifications: "Workflow Notifications",
      all: "All",
      unread: "Unread",
      readFilter: "Read"
    },
    MR: {
      portal: "डीएमए रिपॉझिटरी पोर्टल",
      signOut: "साइन आउट",
      sessionDuration: "सत्र कालावधी",
      todayDate: "आजची तारीख",
      currentTime: "सध्याची वेळ",
      autoLogout: "ऑटो लॉगआउट",
      loading: "लोड होत आहे...",
      logoutWarning: "निष्क्रियतेमुळे तुम्ही लॉग आउट केले जाल",
      profile: "प्रोफाइल",
      settings: "सेटिंग्ज",
      changePassword: "पासवर्ड बदला",
      currentPassword: "सध्याचा पासवर्ड",
      newPassword: "नवीन पासवर्ड",
      confirmPassword: "नवीन पासवर्डची पुष्टी करा",
      verificationMethod: "पडताळणी पद्धत",
      emailVerification: "ईमेल पडताळणी",
      dscVerification: "डीएससी पडताळणी",
      sendCode: "पडताळणी कोड पाठवा",
      verifyAndChange: "पडताळणी करा आणि पासवर्ड बदला",
      verificationCode: "पडताळणी कोड",
      passwordRequirements: "पासवर्ड किमान ८ अक्षरे, एक कॅपिटल अक्षर, एक संख्या आणि एक विशेष वर्ण असणे आवश्यक आहे",
      passwordMismatch: "पासवर्ड जुळत नाहीत",
      passwordSameAsCurrent: "नवीन पासवर्ड सध्याच्या पासवर्ड सारखा असू शकत नाही",
      passwordPreviouslyUsed: "हा पासवर्ड आधी वापरला गेला आहे. कृपया वेगळा पासवर्ड निवडा",
      codeSent: "पडताळणी कोड पाठविला",
      dscInstruction: "कृपया तुमचा डीएससी घाला आणि पुढे जाण्यासाठी 'पडताळणी' वर क्लिक करा",
      cancel: "रद्द करा",
      chooseTheme: "थीम निवडा",
      themes: "थीम्स",
      menu: "मेनू",
      notifications: "सूचना",
      allNotifications: "सर्व सूचना",
      markAllRead: "सर्व वाचल्या म्हणून चिन्हांकित करा",
      noNotifications: "कोणतीही सूचना नाहीत",
      viewAll: "सर्व पहा",
      sentBy: "द्वारे पाठवले",
      markRead: "वाचले म्हणून चिन्हांकित करा",
      read: "वाचले",
      close: "बंद करा",
      new: "नवीन",
      total: "एकूण",
      approved: "मंजूर",
      rejected: "नामंजूर",
      escalated: "वाढविले",
      pending: "प्रलंबित",
      legacyNotifications: "सामान्य सूचना",
      workflowNotifications: "वर्कफ्लो सूचना",
      all: "सर्व",
      unread: "न वाचलेले",
      readFilter: "वाचलेले"
    }
  };

  const currentText = isMarathi ? TEXT.MR : TEXT.EN;

  useEffect(() => {
    addStylesToHead();
  }, []);

  // Handle responsive
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsTablet(tablet);

      if (!mobile && showProfile) {
        setShowProfile(false);
      }
      if (!mobile && mobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showProfile, mobileDrawerOpen]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Update filtered view when filter type or notifications change
  useEffect(() => {
    if (legacyFilterType === "unread") {
      setFilteredLegacyView(legacyAllNotifications.filter(n => !n.is_read));
    } else {
      setFilteredLegacyView(legacyAllNotifications);
    }
  }, [legacyAllNotifications, legacyFilterType]);

  // Auto-cleanup old notifications (older than 7 days) every hour
  useEffect(() => {
    if (!user) return;

    const cleanupOldNotifications = () => {
      setLegacyAllNotifications(prev => {
        const filtered = prev.filter(notif => isWithinLast7Days(notif.created_at));
        if (filtered.length !== prev.length) {
          console.log(`Cleaned up ${prev.length - filtered.length} old notifications older than 7 days`);
        }
        return filtered;
      });

      setLegacyNotifications(prev => {
        const filtered = prev.filter(notif => isWithinLast7Days(notif.created_at));
        return filtered;
      });

      setWorkflowAllNotifications(prev => {
        const filtered = prev.filter(notif => isWithinLast7Days(notif.createdAt));
        if (filtered.length !== prev.length) {
          console.log(`Cleaned up ${prev.length - filtered.length} old workflow notifications older than 7 days`);
        }
        return filtered;
      });
    };

    const cleanupInterval = setInterval(cleanupOldNotifications, 60 * 60 * 1000);
    return () => clearInterval(cleanupInterval);
  }, [user, isWithinLast7Days]);

  // Helper function to get userId from multiple sources
  const getUserId = useCallback(() => {
    let userId = sessionStorage.getItem('userId');
    if (!userId || userId === 'null' || userId === 'undefined' || userId === '') {
      try {
        const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
        userId = userData.id || userData.user_id || null;
        if (userId) {
          sessionStorage.setItem('userId', userId);
        }
      } catch (e) {
        console.error('Error parsing user from sessionStorage:', e);
      }
    }
    if ((!userId || userId === 'null') && user && user.id) {
      userId = user.id;
      sessionStorage.setItem('userId', userId);
    }
    return userId;
  }, [user]);

  // ==================== LEGACY NOTIFICATIONS (Tab 1) ====================
  const getEffectiveUlbId = useCallback(() => {
    if (user?.ulb_id && user.ulb_id !== 'null' && user.ulb_id !== 'undefined') {
      return user.ulb_id;
    }
    const storedUlbId = sessionStorage.getItem('ulbId');
    if (storedUlbId && storedUlbId !== 'null' && storedUlbId !== 'undefined') {
      return storedUlbId;
    }
    try {
      const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (userData.ulb_id) return userData.ulb_id;
    } catch (e) {}
    return null;
  }, [user?.ulb_id]);

  // UPDATED: fetchLegacyNotifications with proper District Admin filtering
  const fetchLegacyNotifications = useCallback(async () => {
    let currentUser = user;

    if (!currentUser) {
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        try {
          currentUser = JSON.parse(storedUser);
        } catch(e) {}
      }
    }

    if (!currentUser) {
      console.log("No user found, skipping legacy notifications fetch");
      return;
    }

    try {
      setLegacyLoading(true);
      const userRole = currentUser?.role_name || currentUser?.role || sessionStorage.getItem('userRole') || 'ULB';
      const districtId = currentUser?.district_id || sessionStorage.getItem('district_id');
      const ulbId = currentUser?.ulb_id || sessionStorage.getItem('ulb_id');
      const userId = currentUser?.id || sessionStorage.getItem('userId');
      const language = isMarathi ? 'mr' : 'en';

      console.log("🔔 Fetching legacy notifications for:", {
        userId, role: userRole, districtId, ulbId
      });

      const params = {
        role: userRole,
        limit: 100,
        _t: Date.now(),
        _nocache: Math.random(),
        language
      };

      if (userRole === 'DISTRICT_ADMIN' || userRole === 'district_admin' || userRole === 'districtadmin') {
        if (districtId && districtId !== 'null' && districtId !== 'undefined') {
          params.district_id = districtId;
          console.log("📌 Adding district_id filter for District Admin:", districtId);
        } else {
          console.warn("⚠️ DISTRICT_ADMIN but no district_id found!");
          setLegacyNotifications([]);
          setLegacyAllNotifications([]);
          setLegacyLoading(false);
          return;
        }
      }

      if (userRole === 'ULB_CHIEF_OFFICIER' || userRole === 'ulb') {
        if (ulbId && ulbId !== 'null' && ulbId !== 'undefined') {
          params.ulbId = ulbId;
        }
      }

      if (userId && userId !== 'null' && userId !== 'undefined') {
        params.userId = userId;
      }

      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/notifications`, {
        params: params,
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-role': userRole,
          'x-user-district': districtId || '',
          'x-user-id': userId || ''
        }
      });

      let notifications = response.data || [];

      if (userRole === 'DISTRICT_ADMIN' || userRole === 'district_admin' || userRole === 'districtadmin') {
        notifications = notifications.filter(notif => {
          if (notif.target_role === 'DISTRICT_ADMIN') return true;
          if (notif.target_location_type === 'district' &&
              String(notif.target_location_id) === String(districtId)) {
            return true;
          }
          return false;
        });
        console.log(`📊 District Admin filter: ${response.data.length} → ${notifications.length} notifications for district ${districtId}`);
      }

      const recentNotifications = notifications.filter(n => isWithinLast7Days(n.created_at));
      console.log(`📬 Received ${notifications.length} notifications, showing last 7 days: ${recentNotifications.length}`);

      setLegacyNotifications(recentNotifications.filter(n => !Boolean(n.is_read)));
      setLegacyAllNotifications(recentNotifications);

      const unreadFiltered = recentNotifications.filter(n => !Boolean(n.is_read)).length;
      setLegacyUnreadCount(unreadFiltered);

    } catch (error) {
      console.error('Failed to fetch legacy notifications:', error);
    } finally {
      setLegacyLoading(false);
    }
  }, [user, isMarathi, API_BASE_URL, isWithinLast7Days]);

  // UPDATED: fetchAllLegacyNotifications with proper District Admin filtering
  const fetchAllLegacyNotifications = useCallback(async () => {
    if (!user) {
      console.log("No user, skipping legacy notifications fetch");
      return;
    }
    try {
      console.log("Fetching all legacy notifications...");
      const effectiveUlbId = getEffectiveUlbId();
      const currentUserId = user?.id || sessionStorage.getItem('userId');
      const language = isMarathi ? 'mr' : 'en';
      const districtId = user?.district_id || sessionStorage.getItem('district_id');
      const userRole = user?.role_name || user?.role || sessionStorage.getItem('userRole') || 'ULB';

      const params = {
        username: user?.username,
        role: userRole,
        language,
        limit: 100,
        _t: Date.now()
      };

      if (userRole === 'DISTRICT_ADMIN' || userRole === 'district_admin') {
        if (districtId && districtId !== 'null' && districtId !== 'undefined') {
          params.district_id = districtId;
          console.log("📌 Fetching for District Admin with district_id:", districtId);
        }
      }

      if (effectiveUlbId && (userRole === 'ulb' || userRole === 'ULB_CHIEF_OFFICIER')) {
        params.ulbId = effectiveUlbId;
      }

      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/notifications`, {
        params: params,
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-role': userRole,
          'x-user-district': districtId || ''
        }
      });

      let filteredData = response.data || [];

      if (userRole === 'DISTRICT_ADMIN' || userRole === 'district_admin') {
        filteredData = filteredData.filter(notif => {
          if (notif.target_role === 'DISTRICT_ADMIN') return true;
          if (notif.target_location_type === 'district' &&
              String(notif.target_location_id) === String(districtId)) {
            return true;
          }
          return false;
        });
        console.log(`📊 District Admin all fetch: ${response.data.length} → ${filteredData.length} notifications`);
      }

      const isDeskUser = userRole === 'dma_desk_officer' || userRole === 'DMA_DESK_OFFICER';
      const isUlbUser = userRole === 'ulb' || userRole === 'ULB_CHIEF_OFFICIER';

      if (isDeskUser) {
        filteredData = filteredData.filter(notif => notif.recipient_type !== 'ulb_specific');
      }
      if (isUlbUser && effectiveUlbId) {
        filteredData = filteredData.filter(notif => {
          if (notif.recipient_type === 'ulb_specific') {
            return String(notif.target_location_id) === String(effectiveUlbId);
          }
          return true;
        });
      }
      if (currentUserId && !isDeskUser && !isUlbUser && userRole !== 'DISTRICT_ADMIN') {
        filteredData = filteredData.filter(notif => {
          if (notif.recipient_type === 'single') {
            return String(notif.recipient_user_id) === String(currentUserId);
          }
          return true;
        });
      }

      const recentNotifications = filteredData.filter(notif => isWithinLast7Days(notif.created_at));
      console.log(`Legacy notifications fetched: ${filteredData.length}, showing last 7 days: ${recentNotifications.length}`);

      setLegacyAllNotifications(recentNotifications);
      const unreadFiltered = recentNotifications.filter(n => !Boolean(n.is_read)).length;
      setLegacyUnreadCount(unreadFiltered);
      setLegacyNotifications(recentNotifications.filter(n => !Boolean(n.is_read)));
    } catch (error) {
      console.error('Failed to fetch all legacy notifications:', error);
    }
  }, [user, isMarathi, API_BASE_URL, getEffectiveUlbId, isWithinLast7Days]);

  // Load legacy notifications when user is available
  useEffect(() => {
    if (user) {
      fetchAllLegacyNotifications();
    }
  }, [user, fetchAllLegacyNotifications]);

  // UPDATED: markLegacyAsRead - Only remove from unread list, keep in all notifications
  const markLegacyAsRead = async (notificationId) => {
    try {
      setLegacyNotifications(prev => prev.filter(n => n.id !== notificationId));
      setLegacyAllNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, is_read: 1 } : n
      ));
      setLegacyUnreadCount(prev => Math.max(0, prev - 1));

      const effectiveUlbId = getEffectiveUlbId();
      const params = { username: user?.username, role: user?.role };
      if (effectiveUlbId && (user?.role === 'ulb' || user?.role === 'ULB_CHIEF_OFFICIER')) {
        params.ulbId = effectiveUlbId;
      }

      const token = sessionStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/notifications/mark-read/${notificationId}`, null, {
        params: params,
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking legacy notification as read:', error);
    }
  };

  const markAllLegacyAsRead = async () => {
    try {
      setLegacyNotifications([]);
      setLegacyAllNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setLegacyUnreadCount(0);

      const effectiveUlbId = getEffectiveUlbId();
      const params = { username: user?.username, role: user?.role };
      if (effectiveUlbId && (user?.role === 'ulb' || user?.role === 'ULB_CHIEF_OFFICIER')) {
        params.ulbId = effectiveUlbId;
      }

      const token = sessionStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/notifications/mark-all-read`, null, {
        params: params,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      message.success(isMarathi ? 'सर्व नोटिफिकेशन्स वाचले म्हणून चिन्हांकित केले' : 'All notifications marked as read');
    } catch (error) {
      console.error('Error marking all legacy as read:', error);
    }
  };

  // ==================== WORKFLOW NOTIFICATIONS (Tab 2) ====================
  const fetchWorkflowNotifications = useCallback(async () => {
    try {
      setWorkflowLoading(true);
      const token = sessionStorage.getItem("token");
      let userRole = sessionStorage.getItem('userRole') || 'ULB';
      const userId = getUserId();
      const userDivision = sessionStorage.getItem('division_id') || sessionStorage.getItem('division');
      const userDistrict = sessionStorage.getItem('district_id') || sessionStorage.getItem('district');
      const userDeskId = sessionStorage.getItem('desk_id') || sessionStorage.getItem('deskId');

      const response = await axios.get(`${API_BASE}/workflow/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-user-role': userRole,
          'x-user-id': userId || '',
          'x-user-district': userDistrict || '',
          'x-user-division': userDivision || '',
          'x-user-desk-id': userDeskId || ''
        }
      });

      if (response.data.success) {
        let workflowData = response.data.data || [];
        workflowData = workflowData.filter(notif => isWithinLast7Days(notif.createdAt));
        setWorkflowAllNotifications(workflowData);

        const totalUnread = legacyUnreadCount + workflowData.filter(n => !n.is_read).length;
        setUnreadCount(totalUnread);
        console.log(`🔔 Workflow notifications loaded: ${workflowData.length}, unread: ${workflowData.filter(n => !n.is_read).length}, total unread: ${totalUnread}`);
      }
    } catch (error) {
      console.error("Error fetching workflow notifications:", error);
    } finally {
      setWorkflowLoading(false);
    }
  }, [getUserId, isWithinLast7Days, legacyUnreadCount]);

  const markWorkflowAsRead = async (notificationId) => {
    try {
      const token = sessionStorage.getItem("token");
      let userRole = sessionStorage.getItem('userRole') || 'ULB';
      const userId = getUserId();

      await axios.post(`${API_BASE}/workflow/notifications/${notificationId}/read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-user-role': userRole,
          'x-user-id': userId || ''
        }
      });

      setWorkflowAllNotifications(prev => prev.map(notif =>
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      ));
    } catch (error) {
      console.error("Error marking workflow as read:", error);
    }
  };

  const markAllWorkflowAsRead = async () => {
    try {
      const unreadNotifications = workflowAllNotifications.filter(n => !n.is_read);
      for (const notif of unreadNotifications) {
        await markWorkflowAsRead(notif.id);
      }
      message.success(isMarathi ? 'सर्व वर्कफ्लो नोटिफिकेशन्स वाचले म्हणून चिन्हांकित केले' : 'All workflow notifications marked as read');
    } catch (error) {
      console.error("Error marking all workflow as read:", error);
    }
  };

  const getFilteredWorkflowNotifications = useCallback(() => {
    if (workflowFilterType === 'all') {
      return workflowAllNotifications;
    } else if (workflowFilterType === 'unread') {
      return workflowAllNotifications.filter(n => !n.is_read);
    } else if (workflowFilterType === 'read') {
      return workflowAllNotifications.filter(n => n.is_read);
    }
    return workflowAllNotifications;
  }, [workflowAllNotifications, workflowFilterType]);

  const getWorkflowSummary = useCallback(() => {
    const total = workflowAllNotifications.length;
    const approved = workflowAllNotifications.filter(n => n.type === 'Approved').length;
    const rejected = workflowAllNotifications.filter(n => n.type === 'Rejected').length;
    const escalated = workflowAllNotifications.filter(n => n.type === 'Escalated').length;
    const pending = workflowAllNotifications.filter(n => n.type === 'Pending').length;
    return { total, approved, rejected, escalated, pending };
  }, [workflowAllNotifications]);

  const getWorkflowIconByType = (type) => {
    switch (type) {
      case 'Approved': return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
      case 'Rejected': return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />;
      case 'Re-Approved': return <CheckCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />;
      case 'Pending': return <ClockCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />;
      case 'Escalated': return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />;
      default: return <BellOutlined style={{ color: '#666', fontSize: 18 }} />;
    }
  };

  const getWorkflowTagByType = (type) => {
    const config = {
      'Approved': { color: 'success', icon: <CheckCircleOutlined /> },
      'Rejected': { color: 'error', icon: <CloseCircleOutlined /> },
      'Re-Approved': { color: 'warning', icon: <CheckCircleOutlined /> },
      'Pending': { color: 'processing', icon: <ClockCircleOutlined /> },
      'Escalated': { color: 'orange', icon: <ExclamationCircleOutlined /> }
    };
    const cfg = config[type] || { color: 'default', icon: <BellOutlined /> };
    return <AntTag color={cfg.color} icon={cfg.icon}>{type}</AntTag>;
  };

  const getPriorityColor = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'high': case 'urgent': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getPriorityLabel = (priority) => {
    const priorityMap = { 'low': 'Low', 'medium': 'Medium', 'high': 'High', 'urgent': 'Urgent' };
    return priorityMap[priority?.toLowerCase()] || priority;
  };

  const categories = [
    { value: 'general', label_en: 'General', label_mr: 'सामान्य' },
    { value: 'system', label_en: 'System Update', label_mr: 'सिस्टीम अपडेट' },
    { value: 'form_assignment', label_en: 'Form Assignment', label_mr: 'फॉर्म नियुक्ती' },
    { value: 'form_rejection', label_en: 'Form Rejection', label_mr: 'फॉर्म नकार' },
    { value: 'form_acceptance', label_en: 'Form Acceptance', label_mr: 'फॉर्म स्वीकार' },
    { value: 'policy', label_en: 'Policy', label_mr: 'धोरण' },
    { value: 'deadline', label_en: 'Deadline', label_mr: 'शेवटची तारीख' },
    { value: 'announcement', label_en: 'Announcement', label_mr: 'जाहिरात' },
    { value: 'reminder', label_en: 'Reminder', label_mr: 'रिमाइंडर' }
  ];

  const handleWorkflowNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setDetailModalVisible(true);
    if (!notification.is_read) {
      markWorkflowAsRead(notification.id);
    }
  };

  // ==================== COMMON FUNCTIONS ====================
  useEffect(() => {
    if (user) {
      fetchLegacyNotifications();
      fetchWorkflowNotifications();
    }
  }, [user, fetchLegacyNotifications, fetchWorkflowNotifications]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchLegacyNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchLegacyNotifications, user]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchWorkflowNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchWorkflowNotifications, user]);

  // Fetch groups with role-based menu access
  const fetchGroupedButtons = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token");
      const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
      const role = userData.role_name || userData.role || 'SUPER_ADMIN';

      const roleAccessRes = await axios.get(`${API_BASE}/role-menu-access`, {
        headers: { 'x-user-role': role }
      });

      const accessibleMenuIds = new Set();
      if (roleAccessRes.data && roleAccessRes.data.menu_access) {
        roleAccessRes.data.menu_access.forEach(id => accessibleMenuIds.add(id));
      }

      const groupsRes = await axios.get(`${API_BASE}/sidebar-groups`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-user-role': role
        }
      });

      if (groupsRes.data && Array.isArray(groupsRes.data)) {
        const validGroups = groupsRes.data.filter(group => {
          if (!group.buttons || group.buttons.length === 0) return false;
          const hasAccessibleButton = group.buttons.some(button => accessibleMenuIds.has(button.id));
          const accessibleButtons = group.buttons.filter(button => accessibleMenuIds.has(button.id));
          group.buttons = accessibleButtons;
          return hasAccessibleButton;
        });

        const sortedGroups = [...validGroups].sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
        setGroupedButtons(sortedGroups);
        console.log('✅ Grouped buttons loaded:', sortedGroups.length);
      } else {
        setGroupedButtons([]);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroupedButtons([]);
    }
  }, []);

  const loadNavbarTheme = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/navbar-theme`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success && response.data.navbarTheme) {
        setNavbarTheme(response.data.navbarTheme);
        sessionStorage.setItem('navbarTheme', JSON.stringify(response.data.navbarTheme));
      } else {
        const savedTheme = sessionStorage.getItem('navbarTheme');
        if (savedTheme) {
          setNavbarTheme(JSON.parse(savedTheme));
        }
      }
    } catch (error) {
      console.error('Error loading navbar theme:', error);
      const savedTheme = sessionStorage.getItem('navbarTheme');
      if (savedTheme) {
        setNavbarTheme(JSON.parse(savedTheme));
      }
    }
  }, []);

  const fetchNavbarTitle = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/navbar-title`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success && response.data.navbarTitle) {
        setNavbarTitle(response.data.navbarTitle);
        sessionStorage.setItem('navbarTitle', response.data.navbarTitle);
      } else {
        const savedTitle = sessionStorage.getItem('navbarTitle');
        if (savedTitle) {
          setNavbarTitle(savedTitle);
        }
      }
    } catch (error) {
      console.error('Error loading navbar title:', error);
      const savedTitle = sessionStorage.getItem('navbarTitle');
      if (savedTitle) {
        setNavbarTitle(savedTitle);
      }
    }
  }, [API_BASE_URL]);

  // ==================== PROFILE LAYOUT FUNCTIONS ====================

  // Fetch profile layout from database
  const fetchProfileLayout = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) return null;

      const response = await axios.get(`${API_BASE_URL}/api/user/profile-layout`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.layout) {
        localStorage.setItem('profile_layout', JSON.stringify(response.data.layout));
        return response.data.layout;
      }
      return null;
    } catch (error) {
      console.error('Error fetching profile layout:', error);
      return null;
    }
  }, [API_BASE_URL]);

  // Get profile layout from localStorage or fetch from DB
  const getProfileLayout = useCallback(async () => {
    const saved = localStorage.getItem('profile_layout');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return await fetchProfileLayout();
  }, [fetchProfileLayout]);

  // Listen for profile layout updates from Admin Portal
  useEffect(() => {
    const handleLayoutUpdate = async () => {
      await fetchProfileLayout();
      setForceUpdate(prev => !prev);
    };

    window.addEventListener('profileLayoutUpdated', handleLayoutUpdate);
    return () => {
      window.removeEventListener('profileLayoutUpdated', handleLayoutUpdate);
    };
  }, [fetchProfileLayout]);

  // Load profile layout on mount
  useEffect(() => {
    if (user) {
      getProfileLayout();
    }
  }, [user, getProfileLayout]);

  // Available widgets for profile layout
  const PROFILE_WIDGETS = [
    { id: 'user_avatar', label: 'User Avatar', icon: '👤' },
    { id: 'user_name', label: 'User Name', icon: '📛' },
    { id: 'user_role', label: 'User Role', icon: '🎯' },
    { id: 'user_info', label: 'User Info', icon: '📋' },
    { id: 'change_password', label: 'Change Password', icon: '🔑' },
    { id: 'admin_portal', label: 'Admin Portal', icon: '⚙️' },
    { id: 'sign_out', label: 'Sign Out', icon: '🚪' },
    { id: 'session_info', label: 'Session Info', icon: '⏱️' },
    { id: 'notification_badge', label: 'Notification Badge', icon: '🔔' },
    { id: 'divider', label: 'Divider Line', icon: '➖' },
  ];

  // Render widget content based on widgetId
  const renderWidgetContent = (widgetId) => {
    switch(widgetId) {
      case 'user_avatar':
        return (
          <div style={{ textAlign: 'center', padding: '4px' }}>
            <Avatar
              style={{
                background: getRoleColor(user?.role_name || user?.role),
                width: '50px',
                height: '50px',
                fontSize: '20px',
                border: '2px solid rgba(255,255,255,0.8)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}
            >
              {(user?.full_name || user?.username || 'U')?.charAt(0)?.toUpperCase()}
            </Avatar>
          </div>
        );

      case 'user_name':
        return (
          <div style={{ textAlign: 'center', padding: '4px' }}>
            <Title level={5} style={{ margin: '0', color: '#1a237e', fontSize: '14px', fontWeight: 700 }}>
              {user?.full_name || user?.username || 'User'}
            </Title>
          </div>
        );

      case 'user_role':
        return (
          <div style={{ textAlign: 'center', padding: '4px' }}>
            <AntTag color={getRoleColor(user?.role_name || user?.role)} style={{ fontSize: '11px', padding: '4px 10px' }}>
              {user?.role_name || user?.role || 'User'}
            </AntTag>
          </div>
        );

      case 'user_info':
        return (
          <div className="user-info-scroll" style={{
            fontSize: '13px',
            textAlign: 'left',
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '8px 12px',
            maxHeight: '200px',
            overflowY: 'auto',
            width: '100%',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {user && Object.entries(user).filter(([key, value]) => {
              const excludeFields = [
                'id', 'user_id', 'role_id', 'password', 'token', 'access_token',
                'iat', 'exp', 'roleid', 'table_name', 'tableName',
                'has_form_builder_access', 'must_change_password', 'mustChangePassword',
                'desk_ids', 'workflow_role', 'workflowRole',
                'district_id', 'division_id', 'district_name_mr', 'division_name_mr'
              ];
              return !excludeFields.includes(key) && typeof value !== 'object' && value !== null && value !== undefined;
            }).map(([key, value]) => {
              let displayValue = String(value);
              return (
                <div key={key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  borderBottom: '1px solid #eee',
                  padding: '6px 0',
                  gap: '8px'
                }}>
                  <span style={{
                    fontWeight: 600,
                    color: theme.primary,
                    fontSize: '12px',
                    minWidth: '120px',
                    flexShrink: 0
                  }}>
                    {key.replace(/_/g, ' ')}:
                  </span>
                  <span style={{
                    color: '#333',
                    fontSize: '12px',
                    textAlign: 'right',
                    wordBreak: 'break-word',
                    flex: 1
                  }}>
                    {displayValue}
                  </span>
                </div>
              );
            })}
            {(!user || Object.entries(user).filter(([key, value]) => {
              const excludeFields = [
                'id', 'user_id', 'role_id', 'password', 'token', 'access_token',
                'iat', 'exp', 'roleid', 'table_name', 'tableName',
                'has_form_builder_access', 'must_change_password', 'mustChangePassword',
                'desk_ids', 'workflow_role', 'workflowRole'
              ];
              return !excludeFields.includes(key) && typeof value !== 'object' && value !== null && value !== undefined;
            }).length === 0) && (
              <div style={{ textAlign: 'center', color: '#999', padding: '10px 0' }}>
                No user information available
              </div>
            )}
          </div>
        );

      case 'change_password':
        return (
          <div style={{ padding: '4px', width: '100%' }}>
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={openChangePasswordModal}
              style={{
                width: '100%',
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.iconColor})`,
                border: "none",
                borderRadius: "6px",
                fontWeight: 600,
                height: '32px',
                fontSize: '11px'
              }}
            >
              Change Password
            </Button>
          </div>
        );

      case 'admin_portal':
        if (user?.role_name === 'SUPER_ADMIN' || user?.role === 'SUPER_ADMIN') {
          return (
            <div style={{ padding: '4px', width: '100%' }}>
              <Button
                icon={<SettingOutlined />}
                onClick={() => {
                  navigate('/admin-portal');
                  setShowProfile(false);
                }}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #0f766e, #0f172a)',
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  color: '#ffffff',
                  height: '32px',
                  fontSize: '11px'
                }}
              >
                Admin Portal
              </Button>
            </div>
          );
        }
        return null;

      case 'sign_out':
        return (
          <div style={{ padding: '4px', width: '100%' }}>
            <Button
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                width: '100%',
                height: '32px',
                fontWeight: 600,
                fontSize: '11px',
                background: '#dc2626',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff'
              }}
            >
              {currentText.signOut}
            </Button>
          </div>
        );

      case 'session_info':
        return (
          <div style={{ fontSize: '10px', color: '#666', textAlign: 'center', width: '100%', padding: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '2px 0' }}>
              <span>Session:</span>
              <span>{String(screenTime.hours).padStart(2, '0')}:{String(screenTime.minutes).padStart(2, '0')}:{String(screenTime.seconds).padStart(2, '0')}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span>Logout in:</span>
              <span>{Math.ceil(inactivityTimeLeft / 1000)}s</span>
            </div>
          </div>
        );

      case 'notification_badge':
        return (
          <div style={{ textAlign: 'center', padding: '4px' }}>
            <Badge count={unreadCount} style={{ backgroundColor: theme.primary }}>
              <BellOutlined style={{ fontSize: '24px', color: '#666' }} />
            </Badge>
            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
              {unreadCount > 0 ? `${unreadCount} unread` : 'No notifications'}
            </div>
          </div>
        );

      case 'divider':
        return <Divider style={{ margin: '4px 0' }} />;

      default:
        return <div style={{ fontSize: '11px', color: '#ccc', textAlign: 'center' }}>Empty</div>;
    }
  };

  const fetchSidebar = useCallback(async () => {
    try {
      setMenuLoading(true);
      const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
      const role = userData.role_name || userData.role || 'SUPER_ADMIN';
      const res = await axios.get(`${API_BASE}/sidebar`, {
        headers: { 'x-user-role': role }
      });
      setMenuItems(Array.isArray(res.data) ? res.data : []);
      console.log('✅ Sidebar items loaded:', res.data.length);
    } catch (error) {
      console.error("Sidebar fetch error:", error);
      setMenuItems([]);
    } finally {
      setMenuLoading(false);
    }
  }, []);

  const callLogoutAPI = useCallback(async (reason = "manual") => {
    try {
      const token = sessionStorage.getItem("token");
      const userData = sessionStorage.getItem("user");

      if (token && userData) {
        const user = JSON.parse(userData);
        const username = user.username || user.full_name || "unknown";

        await fetch(`${API_BASE_URL}/api/login/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ username })
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleAutoLogout = useCallback(async () => {
    await callLogoutAPI("auto");
    sessionStorage.clear();
    navigate("/login", { replace: true });
  }, [navigate, callLogoutAPI]);

  const resetInactivityTimer = useCallback(() => {
    if (!document.hidden) {
      lastActivityRef.current = Date.now();
      setInactivityTimeLeft(INACTIVITY_TIMEOUT);
      setIsWarningVisible(false);

      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }

      inactivityTimerRef.current = setInterval(() => {
        if (!document.hidden) {
          const elapsed = Date.now() - lastActivityRef.current;
          const timeLeft = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
          setInactivityTimeLeft(timeLeft);

          if (timeLeft <= 3000) setIsWarningVisible(true);
          if (timeLeft <= 0) {
            handleAutoLogout();
          }
        }
      }, 1000);
    }
  }, [handleAutoLogout]);

  const handleLogout = async () => {
    await callLogoutAPI("manual");
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const handleActivity = () => { resetInactivityTimer(); };
    ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'].forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });
    resetInactivityTimer();
    return () => {
      ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'].forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return;

    let visibilityTimerInterval = null;
    let tabHiddenTime = null;
    const HIDDEN_TIMEOUT = 5 * 60 * 1000;
    let warningShownForTab = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabHiddenTime = Date.now();
        warningShownForTab = false;

        if (visibilityTimerInterval) clearInterval(visibilityTimerInterval);
        visibilityTimerInterval = setInterval(() => {
          if (document.hidden && tabHiddenTime) {
            const hiddenDuration = Date.now() - tabHiddenTime;

            if (!warningShownForTab && hiddenDuration >= (HIDDEN_TIMEOUT - 60000)) {
              warningShownForTab = true;
              message.warning('तुम्ही दुसऱ्या टॅबवर असल्यामुळे 1 मिनिटात logout व्हाल', 30);
            }

            if (hiddenDuration >= HIDDEN_TIMEOUT) {
              clearInterval(visibilityTimerInterval);
              handleAutoLogout();
            }
          }
        }, 1000);
      } else {
        if (tabHiddenTime) {
          const hiddenDuration = Date.now() - tabHiddenTime;
          if (hiddenDuration < HIDDEN_TIMEOUT) {
            resetInactivityTimer();
          }
          tabHiddenTime = null;
        }
        if (visibilityTimerInterval) {
          clearInterval(visibilityTimerInterval);
          visibilityTimerInterval = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimerInterval) clearInterval(visibilityTimerInterval);
    };
  }, [handleAutoLogout, resetInactivityTimer]);

  useEffect(() => {
    sessionStartRef.current = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      const seconds = elapsed % 60;
      setScreenTime({ hours, minutes, seconds });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token");
      const storedUser = sessionStorage.getItem("user");

      if (token) {
        const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-user-role': sessionStorage.getItem('userRole') || 'SUPER_ADMIN'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          sessionStorage.setItem("user", JSON.stringify(userData));

          if (userData.id || userData.user_id) {
            const userId = userData.id || userData.user_id;
            sessionStorage.setItem('userId', userId);
          }

          setLoading(false);
          resetInactivityTimer();
          return;
        }
      }

      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);

        if (userData.id || userData.user_id) {
          const userId = userData.id || userData.user_id;
          sessionStorage.setItem('userId', userId);
        }

        setLoading(false);
        resetInactivityTimer();
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error("Profile fetch error:", error);
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        if (userData.id || userData.user_id) {
          const userId = userData.id || userData.user_id;
          sessionStorage.setItem('userId', userId);
        }
      }
      setLoading(false);
    }
  }, [resetInactivityTimer]);

  useEffect(() => {
    fetchUserProfile();
    fetchSidebar();
    fetchGroupedButtons();
    loadNavbarTheme();
    fetchNavbarTitle();
  }, [fetchUserProfile, fetchSidebar, fetchGroupedButtons, loadNavbarTheme, fetchNavbarTitle]);

  useEffect(() => {
    const workflowUnread = workflowAllNotifications.filter(n => !n.is_read).length;
    const totalUnread = legacyUnreadCount + workflowUnread;
    setUnreadCount(totalUnread);
    console.log(`🔄 Synced unread count: Legacy=${legacyUnreadCount}, Workflow unread=${workflowUnread}, Total=${totalUnread}`);
  }, [legacyUnreadCount, workflowAllNotifications]);

  const getButtonIcon = (buttonName) => {
    const name = buttonName.toLowerCase();
    if (name.includes('transfer')) return <UserOutlined />;
    if (name.includes('status')) return <ClockCircleOutlined />;
    if (name.includes('form')) return <FileOutlined />;
    if (name.includes('builder')) return <SettingOutlined />;
    if (name.includes('filling')) return <MailOutlined />;
    if (name.includes('validation')) return <SafetyCertificateOutlined />;
    if (name.includes('report')) return <BarChartOutlined />;
    if (name.includes('analysis')) return <CalendarOutlined />;
    if (name.includes('submission')) return <BellOutlined />;
    return <FolderOutlined />;
  };

  const renderGroupedButtons = () => {
    if (!groupedButtons || groupedButtons.length === 0) {
      return null;
    }

    return groupedButtons.map((group) => {
      const buttons = group.buttons || [];
      const groupName = group.display_name || group.name || "Menu";

      if (buttons.length === 0) return null;

      const menuItemsList = buttons.map((button, idx) => ({
        key: button.id || idx,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {getButtonIcon(button.name || button.title)}
            <span style={{ color: "#1A2B4C", fontSize: "13px", fontWeight: 500 }}>
              {button.name || button.title}
            </span>
          </div>
        ),
        onClick: () => {
          const buttonPath = button.route || button.path || button.url || `/${button.name?.toLowerCase().replace(/\s+/g, '-')}`;
          navigate(buttonPath);
          // Close mobile drawer if open
          setMobileDrawerOpen(false);
        }
      }));

      return (
        <Dropdown
          key={group.id || groupName}
          menu={{
            items: menuItemsList,
            style: {
              background: "#FFFFFF",
              borderRadius: "8px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "1px solid #E2E8F0",
              padding: "8px 0"
            }
          }}
          trigger={['hover']}
          placement="bottom"
          overlayStyle={{ minWidth: '180px', maxWidth: '280px', zIndex: 1050 }}
          overlayClassName="group-dropdown-custom"
        >
          <Button
            className="group-button"
            style={{
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              height: "34px",
              background: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              fontWeight: 500,
              fontSize: isMobile ? "12px" : "13px",
              padding: isMobile ? "0 10px" : "0 14px",
              transition: "all 0.2s ease",
              cursor: "pointer",
              whiteSpace: "nowrap"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
            }}
          >
            {groupName}
            <DownOutlined style={{ fontSize: "10px", color: "#FFFFFF" }} />
          </Button>
        </Dropdown>
      );
    });
  };

  const convertToAntMenuItems = (items) => {
    if (!items || !items.length) return [];
    return items.map((item) => {
      const menuItem = { key: item.route || item.id, label: item.title };
      if (item.children && item.children.length > 0) {
        menuItem.children = convertToAntMenuItems(item.children);
      }
      return menuItem;
    });
  };

  const renderMobileMenuItems = () => {
    const items = [];

    if (groupedButtons && groupedButtons.length > 0) {
      groupedButtons.forEach(group => {
        const buttons = group.buttons || [];
        const groupName = group.name || group.group_name || "Menu";

        if (buttons.length === 1) {
          const button = buttons[0];
          items.push({
            key: `group_${group.id}`,
            icon: getButtonIcon(button.name || button.title),
            label: button.name || button.title,
            onClick: () => {
              const path = button.route || button.path || button.url || `/${button.name?.toLowerCase().replace(/\s+/g, '-')}`;
              navigate(path);
              setMobileDrawerOpen(false);
            }
          });
        } else {
          items.push({
            key: `group_${group.id}`,
            icon: <AppstoreOutlined />,
            label: groupName,
            children: buttons.map(button => ({
              key: button.id,
              icon: getButtonIcon(button.name || button.title),
              label: button.name || button.title,
              onClick: () => {
                const path = button.route || button.path || button.url || `/${button.name?.toLowerCase().replace(/\s+/g, '-')}`;
                navigate(path);
                setMobileDrawerOpen(false);
              }
            }))
          });
        }
      });
    }

    if (items.length > 0 && menuItems.length > 0) {
      items.push({ key: 'divider', type: 'divider' });
    }

    const addMenuItems = (menuItemList) => {
      menuItemList.forEach(item => {
        if (item.children && item.children.length > 0) {
          items.push({
            key: item.key || item.route,
            icon: <FolderOutlined />,
            label: item.label || item.title,
            children: addMenuItems(item.children)
          });
        } else {
          items.push({
            key: item.key || item.route,
            icon: <FolderOutlined />,
            label: item.label || item.title,
            onClick: () => {
              if (item.route) {
                navigate(item.route);
                setMobileDrawerOpen(false);
              }
            }
          });
        }
      });
      return [];
    };

    if (menuItems.length > 0) {
      addMenuItems(menuItems.map(item => ({
        key: item.route || item.id,
        label: item.title,
        route: item.route,
        children: item.children
      })));
    }

    return items;
  };

  const antdMenuItems = convertToAntMenuItems(menuItems);
  const selectedKey = location.pathname;

  const toggleProfile = () => {
    if (!loading) {
      setShowProfile(prev => !prev);
      resetInactivityTimer();
    }
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Minimum 8 characters required";
    if (!/[A-Z]/.test(password)) return "At least one uppercase letter required";
    if (!/[0-9]/.test(password)) return "At least one number required";
    if (!/[!@#$%^&*]/.test(password)) return "At least one special character required";
    return "";
  };

  const checkPasswordHistory = async (username, newPassword) => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/check-password-history`,
        { username, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.isUsed;
    } catch (error) {
      console.error("Error checking password history:", error);
      return false;
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    const validationError = validatePassword(value);
    setPasswordError(validationError);
    if (value && value === currentPassword) {
      setPasswordError(currentText.passwordSameAsCurrent);
    }
    if (confirmNewPassword && value !== confirmNewPassword) {
      setConfirmPasswordError(currentText.passwordMismatch);
    } else if (confirmNewPassword && value === confirmNewPassword) {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmNewPassword(value);
    if (value && value !== newPassword) {
      setConfirmPasswordError(currentText.passwordMismatch);
    } else {
      setConfirmPasswordError("");
    }
  };

  const openChangePasswordModal = () => {
    setIsChangePasswordModalVisible(true);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordError("");
    setConfirmPasswordError("");
    setVerificationCode("");
    setIsCodeSent(false);
    setVerificationCodeSent("");
    setPasswordVerificationMethod("email");
    resetInactivityTimer();
  };

  const closeChangePasswordModal = () => {
    setIsChangePasswordModalVisible(false);
  };

  const sendVerificationCode = async () => {
    if (!currentPassword) {
      message.error("Please enter your current password");
      return;
    }

    setIsVerifying(true);
    try {
      const token = sessionStorage.getItem("token");
      const username = user?.username;

      const response = await axios.post(
        `${API_BASE_URL}/api/auth/send-verification-code`,
        { username, method: passwordVerificationMethod, current_password: currentPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setIsCodeSent(true);
        setVerificationCodeSent(response.data.code || "");
        message.success("Verification code generated successfully!");
      } else {
        message.error(response.data.error || "Failed to send verification code");
      }
    } catch (error) {
      console.error("Error sending verification code:", error);
      message.error(error.response?.data?.error || "Failed to send verification code");
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyAndChangePassword = async () => {
    if (!currentPassword) {
      message.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      message.error("Please enter a new password");
      return;
    }
    if (!confirmNewPassword) {
      message.error("Please confirm your new password");
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      message.error(validationError);
      return;
    }
    if (newPassword === currentPassword) {
      message.error(currentText.passwordSameAsCurrent);
      return;
    }
    if (newPassword !== confirmNewPassword) {
      message.error(currentText.passwordMismatch);
      return;
    }
    if (passwordVerificationMethod === "email" && !verificationCode) {
      message.error("Please enter the verification code");
      return;
    }

    setIsVerifying(true);
    try {
      const token = sessionStorage.getItem("token");
      const username = user?.username;

      const isPasswordUsed = await checkPasswordHistory(username, newPassword);
      if (isPasswordUsed) {
        message.error(currentText.passwordPreviouslyUsed);
        setIsVerifying(false);
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/auth/change-password`,
        {
          username,
          current_password: currentPassword,
          new_password: newPassword,
          verification_method: passwordVerificationMethod,
          verification_code: passwordVerificationMethod === "email" ? verificationCode : null,
          dsc_verified: passwordVerificationMethod === "dsc"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success("Password changed successfully");
        closeChangePasswordModal();

        confirm({
          title: "Password Changed",
          content: "For security reasons, you will be logged out. Please log in with your new password.",
          okText: "OK",
          cancelText: "Cancel",
          onOk: handleLogout
        });
      } else {
        message.error(response.data.error || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      message.error(error.response?.data?.error || "Failed to change password");
    } finally {
      setIsVerifying(false);
    }
  };

  const getRoleColor = (role) => {
    const map = {
      super_admin: "#ff4d4f",
      ulb: "#52c41a",
      district_admin: "#faad14",
      divisional_officer: "#1890ff",
      dma_desk_officer: "#722ed1",
      'SUPER_ADMIN': '#ff4d4f'
    };
    return map[role?.toLowerCase()] || "#1890ff";
  };

  const getFormattedDate = () => {
    const today = new Date();
    if (isMarathi) {
      const day = today.getDate();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      return `${day} / ${month} / ${year}`;
    } else {
      return today.toLocaleDateString('en-IN', {
        weekday: isMobile ? 'short' : 'long',
        year: 'numeric',
        month: isMobile ? 'short' : 'long',
        day: 'numeric'
      });
    }
  };

  const getFormattedTime = () => {
    const today = new Date();
    if (isMarathi) {
      const hours = today.getHours().toString().padStart(2, '0');
      const minutes = today.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      return today.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const formattedDate = getFormattedDate();
  const formattedTime = getFormattedTime();

  const mobileMenuItemsList = [
    { key: 'profile', icon: <UserOutlined />, label: currentText.profile, onClick: toggleProfile },
    { key: 'changePassword', icon: <KeyOutlined />, label: currentText.changePassword, onClick: openChangePasswordModal },
    { key: 'logout', icon: <LogoutOutlined />, label: currentText.signOut, danger: true, onClick: handleLogout }
  ];

  const MobileProfileDropdown = () => (
    <Dropdown
      menu={{ items: mobileMenuItemsList }}
      trigger={['click']}
      placement="bottomRight"
      open={showProfile}
      onOpenChange={setShowProfile}
      overlayStyle={{ minWidth: '200px', maxWidth: '300px' }}
    >
      <Avatar
        size={36}
        style={{
          background: user ? getRoleColor(user.role_name || user.role) : '#1890ff',
          cursor: 'pointer',
          border: '2px solid rgba(255,255,255,0.3)',
          boxShadow: '0 0 10px rgba(255,255,255,0.3)'
        }}
      >
        {user ? (user?.full_name || user?.username || 'U')?.charAt(0)?.toUpperCase() : 'U'}
      </Avatar>
    </Dropdown>
  );

  const getUserInfoForNotifications = () => ({
    id: getUserId(),
    name: user?.full_name || user?.username,
    role: user?.role_name || user?.role || 'ULB',
    roleType: (user?.role_name || user?.role || 'ULB').toUpperCase(),
    district: sessionStorage.getItem('district_id') || user?.district_id,
    division: sessionStorage.getItem('division_id') || user?.division_id,
    deskId: sessionStorage.getItem('desk_id') || user?.desk_id
  });

  // ==================== RENDER LEGACY NOTIFICATION LIST ====================
  const renderLegacyNotificationList = (notificationsList, isCompact = false) => (
    <List
      itemLayout="vertical"
      dataSource={notificationsList}
      renderItem={(item) => (
        <div
          key={item.id}
          style={{
            padding: isCompact ? '12px' : '16px',
            marginBottom: '8px',
            borderRadius: '8px',
            background: item.is_read ? '#fafafa' : '#f0f8ff',
            borderLeft: `3px solid ${getPriorityColor(item.priority)}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
            opacity: item.is_read ? 0.8 : 1
          }}
          onClick={() => !item.is_read && markLegacyAsRead(item.id)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Text strong style={{ fontSize: isCompact ? '13px' : '14px', color: item.is_read ? '#666' : '#000' }}>
                {item.title || 'Notification'}
              </Text>
              {!item.is_read && (
                <AntTag color="blue" style={{ fontSize: '10px' }}>{currentText.new}</AntTag>
              )}
              {item.is_read && (
                <AntTag color="default" style={{ fontSize: '10px' }}>{currentText.read}</AntTag>
              )}
              <AntTag color={getPriorityColor(item.priority)} style={{ fontSize: '10px' }}>
                {getPriorityLabel(item.priority)}
              </AntTag>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {!item.is_read && (
                <Button
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={(e) => { e.stopPropagation(); markLegacyAsRead(item.id); }}
                  style={{ fontSize: '11px', height: '24px' }}
                >
                  {currentText.markRead}
                </Button>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <AntTag size="small" style={{ fontSize: '10px' }}>
              {isMarathi ?
                categories.find(c => c.value === item.category)?.label_mr || item.category :
                categories.find(c => c.value === item.category)?.label_en || item.category
              }
            </AntTag>
            {item.target_location_id && item.recipient_type === 'ulb_specific' && (
              <AntTag color="purple" style={{ fontSize: '10px' }}><BankOutlined /> ULB Specific</AntTag>
            )}
            {item.recipient_type === 'single' && (
              <AntTag color="green" style={{ fontSize: '10px' }}><UserOutlined /> Personal</AntTag>
            )}
          </div>

          <Text type="secondary" style={{ fontSize: isCompact ? '12px' : '13px', display: 'block', marginBottom: '8px' }}>
            {item.message || 'No message content'}
          </Text>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#999' }}>
            <span><UserOutlined style={{ marginRight: '4px' }} />{currentText.sentBy}: {item.created_by_name || item.created_by || 'System'}</span>
            <span><CalendarOutlined style={{ marginRight: '4px' }} />{new Date(item.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      )}
    />
  );

  // ==================== RENDER WORKFLOW NOTIFICATION LIST ====================
  const renderWorkflowNotificationList = (items) => (
    <List
      itemLayout="vertical"
      dataSource={items}
      renderItem={(item) => (
        <div
          key={item.id}
          onClick={() => handleWorkflowNotificationClick(item)}
          style={{
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '8px',
            background: item.is_read ? '#fafafa' : '#f0f8ff',
            transition: 'all 0.3s',
            borderLeft: `4px solid ${item.type === 'Approved' ? '#52c41a' : item.type === 'Rejected' ? '#ff4d4f' : item.type === 'Re-Approved' ? '#faad14' : '#1890ff'}`,
            opacity: item.is_read ? 0.8 : 1
          }}
        >
          <List.Item.Meta
            avatar={getWorkflowIconByType(item.type)}
            title={
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Space wrap>
                  <Text strong style={{ color: item.is_read ? '#666' : '#000' }}>{item.title}</Text>
                  {getWorkflowTagByType(item.type)}
                  {!item.is_read && <AntTag color="blue">{currentText.new}</AntTag>}
                  {item.is_read && <AntTag color="default">{currentText.read}</AntTag>}
                </Space>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {item.formName && <span><FileTextOutlined /> {item.formName} </span>}
                  {item.submissionId && <span>| Submission #{item.submissionId}</span>}
                </Text>
              </Space>
            }
            description={
              <div>
                <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 8 }}>
                  {item.message}
                </Paragraph>
                <Space size="small" style={{ fontSize: '12px', color: '#999' }}>
                  <span>{new Date(item.createdAt).toLocaleString()}</span>
                  {item.actionPerformedBy && (
                    <span><UserOutlined /> {item.actionPerformedBy}</span>
                  )}
                  {item.actionPerformedByRole && (
                    <AntTag size="small">{item.actionPerformedByRole}</AntTag>
                  )}
                </Space>
              </div>
            }
          />
        </div>
      )}
    />
  );

  // ==================== OPEN NOTIFICATIONS DRAWER ====================
  const openNotificationsDrawer = () => {
    console.log("Opening notifications drawer");
    setNotificationsDrawerVisible(true);
    fetchAllLegacyNotifications();
    fetchWorkflowNotifications();
    resetInactivityTimer();
  };

  const workflowSummary = getWorkflowSummary();

  return (
    <>
      <Header
        style={{
          position: "fixed",
          zIndex: 1001,
          width: "100%",
          height: isMobile ? "60px" : "68px",
          background: navbarTheme?.navbarBackground || "linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)",
          backgroundSize: "100% 100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 12px" : "0 24px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          transition: "background 0.2s ease",
        }}
      >
        {/* Aurora Overlay Effects */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.05) 0%, transparent 50%)",
          zIndex: 0
        }} />

        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          {Array.from({ length: isMobile ? 5 : 20 }).map((_, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                background: "#ffffff",
                borderRadius: "50%",
                animation: "twinkle 2s ease-in-out infinite",
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.2
              }}
            />
          ))}
        </div>

        {!isMobile && (
          <>
            <div style={{
              position: "absolute",
              top: "-50px",
              left: "-50px",
              width: "150px",
              height: "150px",
              background: `radial-gradient(circle, ${theme.iconColor}40 0%, transparent 70%)`,
              borderRadius: "50%",
              filter: "blur(40px)",
              animation: "glowPulse 4s ease-in-out infinite",
              zIndex: 0
            }} />
            <div style={{
              position: "absolute",
              bottom: "-30px",
              right: "-30px",
              width: "120px",
              height: "120px",
              background: `radial-gradient(circle, ${theme.primary}40 0%, transparent 70%)`,
              borderRadius: "50%",
              filter: "blur(30px)",
              animation: "glowPulse 5s ease-in-out infinite 1s",
              zIndex: 0
            }} />
          </>
        )}

        {/* Left Section: Menu Button + Title */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "10px" : "20px",
          minWidth: 0,
          position: "relative",
          zIndex: 1
        }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileDrawerOpen(true)}
            style={{
              color: "#fff",
              fontSize: isMobile ? "18px" : "20px",
              display: isMobile ? "flex" : "none",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              transition: "all 0.3s ease"
            }}
          />

          <Title
            level={isMobile ? 4 : 3}
            style={{
              margin: 0,
              color: "#ffffff",
              fontSize: isMobile ? "18px" : "28px",
              fontWeight: 800,
              textShadow: "0 2px 15px rgba(0, 0, 0, 0.5), 0 0 30px rgba(255,255,255,0.3)",
              whiteSpace: "nowrap",
              background: "linear-gradient(135deg, #ffffff, #f0f0f0, #ffffff)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "1px",
              fontFamily: "'Poppins', 'Segoe UI', sans-serif"
            }}
          >
            {isMobile ? "DMA" : navbarTitle}
          </Title>
        </div>

        {/* Center Section: Grouped Buttons + Menu Items - Full width with improved visibility */}
        {!isMobile && (
          <div className="navbar-menu-container">
            {renderGroupedButtons()}

            {groupedButtons.length > 0 && antdMenuItems.length > 0 && (
              <div style={{
                width: "1px",
                height: "24px",
                background: "rgba(255,255,255,0.3)",
                margin: "0 4px",
                flexShrink: 0
              }} />
            )}

            {menuLoading ? (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "0 8px" }}>
                <div style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTop: `2px solid ${theme.primary}`,
                  borderRadius: "50%",
                  ...styles.spin
                }} />
                <span style={{ color: "#FFFFFF", fontSize: "13px", opacity: 0.8 }}>{currentText.loading}</span>
              </div>
            ) : (
              antdMenuItems.length > 0 && (
                <ConfigProvider
                  theme={{
                    components: {
                      Menu: {
                        itemBg: "transparent",
                        itemColor: "#84bcf5",
                        itemHoverColor: "#ffffff",
                        itemHoverBg: "rgba(255, 255, 255, 0.15)",
                        itemSelectedBg: "rgba(255, 255, 255, 0.1)",
                        itemSelectedColor: "#ffffff",
                        itemMarginInline: 0,
                        itemBorderRadius: 6,
                        itemHeight: 34,
                        itemPaddingInline: 12,
                        fontSize: 13,
                        activeBarHeight: 0,
                        activeBarBorderWidth: 0,
                      },
                    },
                  }}
                >
                  <AntMenu
                    mode="horizontal"
                    selectedKeys={[selectedKey]}
                    items={antdMenuItems}
                    onClick={(e) => {
                      if (typeof e.key === "string" && e.key.startsWith("/")) {
                        navigate(e.key);
                      } else if (typeof e.key === "string") {
                        navigate(`/${e.key}`);
                      }
                    }}
                    style={{
                      borderBottom: "none",
                      background: "transparent",
                      lineHeight: "34px",
                      height: "34px",
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      minWidth: "auto"
                    }}
                  />
                </ConfigProvider>
              )
            )}

            {/* Show placeholder when no menu items */}
            {!menuLoading && antdMenuItems.length === 0 && groupedButtons.length === 0 && (
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", padding: "0 8px" }}>
                No menu items available
              </div>
            )}
          </div>
        )}

        {/* Right Section: BELL ICON + Profile */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "8px" : "16px",
          flexShrink: 0,
          position: "relative",
          zIndex: 1
        }}>
          {/* BELL ICON - Opens SINGLE TAB drawer */}
          {!isMobile && (
            <div
              style={{
                position: 'relative',
                display: 'inline-block'
              }}
            >
              <Button
                type="text"
                icon={
                  <BellOutlined
                    style={{
                      color: "rgba(255,255,255,0.9)",
                      fontSize: "20px"
                    }}
                  />
                }
                onClick={openNotificationsDrawer}
                style={{
                  color: "#FFFFFF",
                  background: "rgba(255, 255, 255, 0.12)",
                  borderRadius: "8px",
                  height: "40px",
                  width: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              />

              {unreadCount > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#ff4d4f',
                    borderRadius: '50%',
                    border: '2px solid #1E3A5F',
                    zIndex: 9999
                  }}
                />
              )}
            </div>
          )}

          {/* Profile Section */}
          <div ref={profileRef} style={{ position: 'relative' }}>
            <div
              onClick={toggleProfile}
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                opacity: loading ? 0.6 : 1,
                transition: "all 0.3s ease",
                background: "rgba(255, 255, 255, 0.12)",
                backdropFilter: "blur(8px)",
                borderRadius: "12px",
                padding: isMobile ? "4px 6px" : "4px 10px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.12)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              {loading ? (
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: 16,
                    height: 16,
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: `2px solid ${theme.iconColor}`,
                    borderRadius: '50%',
                    ...styles.spin
                  }} />
                </div>
              ) : (
                <>
                  <Avatar
                    style={{
                      background: user ? getRoleColor(user?.role_name || user?.role) : theme.primary,
                      border: "2px solid rgba(255,255,255,0.5)",
                      width: isMobile ? 28 : 32,
                      height: isMobile ? 28 : 32,
                      fontSize: isMobile ? "12px" : "14px",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                      cursor: "pointer"
                    }}
                  >
                    {(user?.full_name || user?.username || 'U')?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  {!isMobile && (
                    showProfile ? (
                      <UpOutlined style={{
                        fontSize: "10px",
                        color: theme.iconColor,
                        cursor: "pointer"
                      }} />
                    ) : (
                      <DownOutlined style={{
                        fontSize: "10px",
                        color: "rgba(255,255,255,0.9)",
                        cursor: "pointer"
                      }} />
                    )
                  )}
                </>
              )}
            </div>

            {/* Profile Dropdown - with Dynamic Grid Layout */}
            {!isMobile && showProfile && user && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '12px',
                width: isTablet ? '400px' : '450px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: '80vh',
                overflowY: 'auto',
                background: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                border: '1px solid #e5e7eb',
                zIndex: 1002,
                ...styles.fadeInDown,
                padding: '16px'
              }}>
                {(() => {
                  const savedLayout = localStorage.getItem('profile_layout');
                  let layout = null;
                  if (savedLayout) {
                    try {
                      layout = JSON.parse(savedLayout);
                    } catch (e) {
                      layout = null;
                    }
                  }

                  const DEFAULT_LAYOUT = [
                    { rowId: 'row_0', columns: 1, widgets: ['user_avatar'] },
                    { rowId: 'row_1', columns: 3, widgets: ['user_name', 'user_role', 'notification_badge'] },
                    { rowId: 'row_2', columns: 1, widgets: ['user_info'] },
                    { rowId: 'row_3', columns: 2, widgets: ['change_password', 'admin_portal'] },
                    { rowId: 'row_4', columns: 1, widgets: ['sign_out'] },
                  ];

                  const currentLayout = layout || DEFAULT_LAYOUT;

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {currentLayout.map((row, rowIndex) => (
                        <React.Fragment key={row.rowId || `row_${rowIndex}`}>
                          {/* Row content */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${row.columns}, 1fr)`,
                            gap: '6px',
                            padding: '4px 0'
                          }}>
                            {Array.from({ length: row.columns }).map((_, colIndex) => {
                              const widgetId = row.widgets[colIndex] || null;

                              return (
                                <div
                                  key={`${row.rowId || rowIndex}_${colIndex}`}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: '0',
                                    padding: '4px',
                                    minHeight: '60px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {widgetId ? renderWidgetContent(widgetId) : (
                                    <div style={{ fontSize: '16px', color: '#ddd' }}></div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Divider between rows - except after the last row */}
                          {rowIndex < currentLayout.length - 1 && (
                            <Divider style={{ margin: '4px 0' }} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* INACTIVITY WARNING BAR */}
        {isWarningVisible && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(255, 243, 205, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid #ffeaa7',
            padding: isMobile ? '6px 12px' : '8px 24px',
            display: 'flex',
            alignItems: "center",
            gap: isMobile ? "8px" : "12px",
            zIndex: 1000,
            ...styles.slideDown,
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            <ExclamationCircleOutlined style={{
              color: '#f39c12',
              fontSize: isMobile ? '14px' : '16px',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{
              fontWeight: 600,
              color: '#856404',
              fontSize: isMobile ? '12px' : '14px',
              flex: 1,
              minWidth: 0
            }}>
              {currentText.logoutWarning} {Math.ceil(inactivityTimeLeft / 1000)} {isMarathi ? "सेकंद" : "seconds"}
            </span>
            <Progress
              percent={Math.round((inactivityTimeLeft / INACTIVITY_TIMEOUT) * 100)}
              size="small"
              showInfo={false}
              strokeColor={{
                '0%': '#f39c12',
                '100%': '#e74c3c',
              }}
              style={{
                flex: isMobile ? '0 0 100%' : 1,
                marginLeft: isMobile ? '0' : '12px',
                marginTop: isMobile ? '4px' : 0
              }}
            />
          </div>
        )}
      </Header>

      {/* Mobile Profile Dropdown */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          top: '64px',
          right: '12px',
          zIndex: 1000
        }}>
          <MobileProfileDropdown />
        </div>
      )}

      {/* Mobile Menu Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar
              size={40}
              style={{
                background: user ? getRoleColor(user.role_name || user.role) : theme.primary
              }}
            >
              {(user?.full_name || user?.username || 'U')?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div>
              <div style={{ fontWeight: 600, color: '#1a237e', fontSize: '16px' }}>
                {user?.full_name || user?.username || 'User'}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {user?.role_name || user?.role || 'Role'}
              </div>
            </div>
          </div>
        }
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        width={280}
        className="mobile-menu-drawer"
        closeIcon={<CloseOutlined style={{ color: '#1a237e', fontSize: '18px' }} />}
        styles={{
          header: { borderBottom: '1px solid #e5e7eb', background: '#ffffff', padding: '16px' },
          body: { padding: 0, background: '#ffffff' }
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <List
            dataSource={renderMobileMenuItems()}
            renderItem={(item) => {
              if (item.type === 'divider') {
                return <Divider style={{ margin: '8px 0' }} />;
              }

              if (item.children) {
                return (
                  <div key={item.key}>
                    <div
                      className="mobile-menu-item"
                      style={{ justifyContent: 'space-between', color: '#1a1a1a', borderBottom: '1px solid #f0f0f0', background: '#ffffff' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {item.icon}
                        <span style={{ color: '#1a1a1a', fontSize: '14px', fontWeight: 500 }}>{item.label}</span>
                      </div>
                      <DownOutlined style={{ fontSize: '12px', color: '#999' }} />
                    </div>
                    <div style={{ paddingLeft: '16px', background: '#fafafa' }}>
                      {item.children.map(child => (
                        <div
                          key={child.key}
                          className="mobile-menu-item"
                          onClick={child.onClick}
                          style={{
                            padding: '12px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            background: '#fafafa'
                          }}
                        >
                          {child.icon}
                          <span style={{ color: '#333', fontSize: '13px' }}>{child.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.key}
                  onClick={item.onClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 20px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    background: location.pathname === item.key ? '#f0f7ff' : '#ffffff',
                    borderLeft: location.pathname === item.key ? `3px solid ${theme.primary}` : '3px solid transparent'
                  }}
                >
                  {item.icon}
                  <span style={{
                    color: location.pathname === item.key ? theme.primary : '#1a1a1a',
                    fontSize: '14px',
                    fontWeight: location.pathname === item.key ? 600 : 500
                  }}>
                    {item.label}
                  </span>
                </div>
              );
            }}
          />

          <Divider style={{ margin: '16px 0' }} />

          {/* Mobile Notification Option */}
          <div
            onClick={() => {
              setMobileDrawerOpen(false);
              openNotificationsDrawer();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 20px',
              borderBottom: '1px solid #f0f0f0',
              cursor: 'pointer',
              background: '#ffffff',
              position: 'relative'
            }}
          >
            <BellOutlined style={{ color: '#1a1a1a' }} />
            <span style={{ color: '#1a1a1a', fontSize: '14px', fontWeight: 500 }}>{currentText.notifications}</span>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                right: '20px',
                background: '#ff4d4f',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold',
                minWidth: '18px',
                height: '18px',
                borderRadius: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px'
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>

          <div onClick={openChangePasswordModal} style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px',
            borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: '#ffffff'
          }}>
            <KeyOutlined style={{ color: '#1a1a1a' }} />
            <span style={{ color: '#1a1a1a', fontSize: '14px', fontWeight: 500 }}>{currentText.changePassword}</span>
          </div>
          <div onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px',
            borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: '#ffffff'
          }}>
            <LogoutOutlined style={{ color: '#dc2626' }} />
            <span style={{ color: '#dc2626', fontSize: '14px', fontWeight: 500 }}>{currentText.signOut}</span>
          </div>
        </div>
      </Drawer>

      {/* ==================== SINGLE MERGED NOTIFICATIONS DRAWER (NO TABS) ==================== */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BellOutlined style={{ color: theme.primary, fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>{currentText.notifications}</span>
            <Badge count={unreadCount} style={{ backgroundColor: theme.primary }} />
          </div>
        }
        placement="right"
        onClose={() => setNotificationsDrawerVisible(false)}
        open={notificationsDrawerVisible}
        width={500}
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchAllLegacyNotifications();
                  fetchWorkflowNotifications();
                }}
              />
            </Tooltip>
          </Space>
        }
      >

        {/* Global Filter Buttons for both types */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <Radio.Group
            value={legacyFilterType}
            onChange={(e) => setLegacyFilterType(e.target.value)}
            size="small"
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              {currentText.all} ({legacyAllNotifications.length + workflowAllNotifications.length})
            </Radio.Button>
            <Radio.Button value="unread">
              {currentText.unread} ({legacyAllNotifications.filter(n => !n.is_read).length + workflowAllNotifications.filter(n => !n.is_read).length})
            </Radio.Button>
          </Radio.Group>

          {(legacyAllNotifications.filter(n => !n.is_read).length > 0 || workflowAllNotifications.filter(n => !n.is_read).length > 0) && (
            <Button size="small" onClick={() => {
              markAllLegacyAsRead();
              markAllWorkflowAsRead();
            }} icon={<CheckOutlined />}>
              {currentText.markAllRead}
            </Button>
          )}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Merged Notifications List */}
        <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
          {/* Legacy Notifications */}
          {legacyLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="small" />
            </div>
          ) : (
            filteredLegacyView.map((item) => (
              <div
                key={`legacy_${item.id}`}
                style={{
                  padding: '16px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  background: item.is_read ? '#fafafa' : '#f0f8ff',
                  borderLeft: `3px solid ${getPriorityColor(item.priority)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: item.is_read ? 0.8 : 1
                }}
                onClick={() => !item.is_read && markLegacyAsRead(item.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <AntTag color="blue">{currentText.legacyNotifications}</AntTag>
                    <Text strong style={{ fontSize: '14px', color: item.is_read ? '#666' : '#000' }}>
                      {item.title || 'Notification'}
                    </Text>
                    {!item.is_read && <AntTag color="blue">{currentText.new}</AntTag>}
                    {item.is_read && <AntTag color="default">{currentText.read}</AntTag>}
                    <AntTag color={getPriorityColor(item.priority)}>{getPriorityLabel(item.priority)}</AntTag>
                  </div>
                  {!item.is_read && (
                    <Button size="small" icon={<CheckOutlined />} onClick={(e) => { e.stopPropagation(); markLegacyAsRead(item.id); }}>
                      {currentText.markRead}
                    </Button>
                  )}
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <AntTag size="small">
                    {isMarathi ?
                      categories.find(c => c.value === item.category)?.label_mr || item.category :
                      categories.find(c => c.value === item.category)?.label_en || item.category
                    }
                  </AntTag>
                </div>
                <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  {item.message}
                </Text>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999' }}>
                  <span><UserOutlined /> {currentText.sentBy}: {item.created_by_name || item.created_by || 'System'}</span>
                  <span><CalendarOutlined /> {new Date(item.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          )}

          {/* Workflow Notifications */}
          {workflowLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="small" />
            </div>
          ) : (
            getFilteredWorkflowNotifications().map((item) => (
              <div
                key={`workflow_${item.id}`}
                onClick={() => handleWorkflowNotificationClick(item)}
                style={{
                  cursor: 'pointer',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  background: item.is_read ? '#fafafa' : '#f0f8ff',
                  transition: 'all 0.3s',
                  borderLeft: `4px solid ${item.type === 'Approved' ? '#52c41a' : item.type === 'Rejected' ? '#ff4d4f' : item.type === 'Re-Approved' ? '#faad14' : '#1890ff'}`,
                  opacity: item.is_read ? 0.8 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <AntTag color="green">{currentText.workflowNotifications}</AntTag>
                    <Text strong style={{ color: item.is_read ? '#666' : '#000' }}>{item.title}</Text>
                    {getWorkflowTagByType(item.type)}
                    {!item.is_read && <AntTag color="blue">{currentText.new}</AntTag>}
                    {item.is_read && <AntTag color="default">{currentText.read}</AntTag>}
                  </div>
                </div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  {item.formName && <span><FileTextOutlined /> {item.formName} </span>}
                  {item.submissionId && <span>| Submission #{item.submissionId}</span>}
                </Text>
                <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 8 }}>
                  {item.message}
                </Paragraph>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999' }}>
                  <span><CalendarOutlined /> {new Date(item.createdAt).toLocaleString()}</span>
                  {item.actionPerformedBy && <span><UserOutlined /> {item.actionPerformedBy}</span>}
                </div>
              </div>
            ))
          )}

          {!legacyLoading && !workflowLoading && filteredLegacyView.length === 0 && getFilteredWorkflowNotifications().length === 0 && (
            <Empty description={currentText.noNotifications} style={{ marginTop: '40px' }} />
          )}
        </div>
      </Drawer>

      {/* Notification Detail Modal for Workflow Notifications */}
      <Modal
        title={
          <Space>
            {selectedNotification && getWorkflowIconByType(selectedNotification.type)}
            <span>Notification Details</span>
            {selectedNotification && getWorkflowTagByType(selectedNotification.type)}
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>Close</Button>
        ]}
        width={500}
      >
        {selectedNotification && (
          <div>
            <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff' }}>
              <Text strong style={{ fontSize: 16 }}>{selectedNotification.title}</Text>
              <Divider style={{ margin: '12px 0' }} />
              <Paragraph>{selectedNotification.message}</Paragraph>
            </Card>

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Form">
                <Text strong>{selectedNotification.formName || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Submission ID">
                <Text code>#{selectedNotification.submissionId}</Text>
              </Descriptions.Item>
              {selectedNotification.actionPerformedBy && (
                <Descriptions.Item label="Action By">
                  <Space>
                    <UserOutlined />
                    <Text>{selectedNotification.actionPerformedBy}</Text>
                    {selectedNotification.actionPerformedByRole && (
                      <AntTag>{selectedNotification.actionPerformedByRole}</AntTag>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              {selectedNotification.actionComments && (
                <Descriptions.Item label="Remarks">
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {selectedNotification.actionComments}
                  </Paragraph>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Received At">
                {new Date(selectedNotification.createdAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* CHANGE PASSWORD MODAL */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <KeyOutlined style={{ color: theme.primary, fontSize: '20px' }} />
            <span>{currentText.changePassword}</span>
          </div>
        }
        open={isChangePasswordModalVisible}
        onCancel={closeChangePasswordModal}
        footer={null}
        width={isMobile ? '95%' : 500}
        centered
        destroyOnClose={true}
        maskClosable={false}
        style={{ zIndex: 2000 }}
      >
        <div style={{ padding: isMobile ? '8px 0' : '16px 0' }}>
          {/* Current Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: isMobile ? '14px' : '15px' }}>
              {currentText.currentPassword} <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <Input.Password
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              size={isMobile ? 'middle' : 'large'}
              prefix={<LockOutlined style={{ color: '#718096' }} />}
              style={{ borderRadius: '8px' }}
            />
          </div>

          {/* Verification Method Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: isMobile ? '14px' : '15px' }}>
              {currentText.verificationMethod}
            </label>
            <Radio.Group
              value={passwordVerificationMethod}
              onChange={(e) => setPasswordVerificationMethod(e.target.value)}
              buttonStyle="solid"
              style={{ width: '100%', display: 'flex', gap: '12px' }}
            >
              <Radio.Button value="email" style={{ flex: 1, textAlign: 'center', borderRadius: '8px', height: isMobile ? '40px' : '44px', lineHeight: isMobile ? '40px' : '44px' }}>
                <MailOutlined /> {currentText.emailVerification}
              </Radio.Button>
              <Radio.Button value="dsc" style={{ flex: 1, textAlign: 'center', borderRadius: '8px', height: isMobile ? '40px' : '44px', lineHeight: isMobile ? '40px' : '44px' }}>
                <SafetyCertificateOutlined /> {currentText.dscVerification}
              </Radio.Button>
            </Radio.Group>
          </div>

          {/* Send Verification Code Button (for email) */}
          {passwordVerificationMethod === 'email' && !isCodeSent && (
            <Button
              type="default"
              onClick={sendVerificationCode}
              loading={isVerifying}
              disabled={!currentPassword}
              icon={<MailOutlined />}
              style={{
                width: '100%',
                marginBottom: '20px',
                height: isMobile ? '40px' : '44px',
                borderRadius: '8px',
                borderColor: theme.primary,
                color: theme.primary
              }}
            >
              {currentText.sendCode}
            </Button>
          )}

          {/* Verification Code Input (for email) */}
          {passwordVerificationMethod === 'email' && isCodeSent && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: isMobile ? '14px' : '15px' }}>
                {currentText.verificationCode} <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                size={isMobile ? 'middle' : 'large'}
                prefix={<MailOutlined style={{ color: '#718096' }} />}
                style={{ borderRadius: '8px' }}
                maxLength={6}
              />

              {verificationCodeSent && (
                <div style={{
                  marginTop: '12px', padding: '12px 16px', background: '#f0f9ff', borderRadius: '8px',
                  border: '2px solid #bae6fd', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailOutlined style={{ color: '#0284c7', fontSize: '18px' }} />
                    <span style={{ fontWeight: 500, color: '#0369a1' }}>Your verification code:</span>
                  </div>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', letterSpacing: '4px', color: '#0284c7', fontFamily: 'monospace', background: 'white', padding: '4px 12px', borderRadius: '6px', border: '1px dashed #0284c7' }}>
                    {verificationCodeSent}
                  </span>
                </div>
              )}

              <div style={{ marginTop: '8px', fontSize: '12px', color: '#718096', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Enter the code above to verify</span>
                <Button type="link" onClick={sendVerificationCode} style={{ padding: 0, height: 'auto' }} size="small" disabled={isVerifying}>Resend Code</Button>
              </div>
            </div>
          )}

          {/* DSC Instruction */}
          {passwordVerificationMethod === 'dsc' && (
            <div style={{ marginBottom: '20px', padding: '16px', background: '#f7fafc', borderRadius: '8px', border: `1px dashed ${theme.primary}`, textAlign: 'center' }}>
              <SafetyCertificateOutlined style={{ fontSize: '32px', color: theme.primary, marginBottom: '8px' }} />
              <p style={{ margin: 0, color: '#2d3748' }}>{currentText.dscInstruction}</p>
            </div>
          )}

          {/* New Password */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: isMobile ? '14px' : '15px' }}>
              {currentText.newPassword} <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <Input.Password
              value={newPassword}
              onChange={handleNewPasswordChange}
              placeholder="Enter new password"
              size={isMobile ? 'middle' : 'large'}
              prefix={<LockOutlined style={{ color: '#718096' }} />}
              style={{ borderRadius: '8px', borderColor: passwordError ? '#e53e3e' : '#d1d5db' }}
            />
            {passwordError && (
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#e53e3e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ExclamationCircleOutlined /> {passwordError}
              </div>
            )}
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#718096' }}>
              {currentText.passwordRequirements}
            </div>
          </div>

          {/* Confirm New Password */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: isMobile ? '14px' : '15px' }}>
              {currentText.confirmPassword} <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <Input.Password
              value={confirmNewPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm new password"
              size={isMobile ? 'middle' : 'large'}
              prefix={<LockOutlined style={{ color: '#718096' }} />}
              style={{ borderRadius: '8px', borderColor: confirmPasswordError ? '#e53e3e' : '#d1d5db' }}
            />
            {confirmPasswordError && (
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#e53e3e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ExclamationCircleOutlined /> {confirmPasswordError}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button onClick={closeChangePasswordModal} size={isMobile ? 'middle' : 'large'} style={{ borderRadius: '8px', minWidth: '100px' }}>
              {currentText.cancel}
            </Button>
            <Button
              type="primary"
              onClick={verifyAndChangePassword}
              loading={isVerifying}
              disabled={
                !currentPassword || !newPassword || !confirmNewPassword ||
                passwordError || confirmPasswordError ||
                (passwordVerificationMethod === 'email' && !verificationCode)
              }
              size={isMobile ? 'middle' : 'large'}
              style={{ borderRadius: '8px', minWidth: '150px', background: `linear-gradient(135deg, ${theme.primary}, ${theme.iconColor})`, border: 'none' }}
            >
              {currentText.verifyAndChange}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Navbar;